import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/motion";

export default function BentoServices() {
  const css = `
    @keyframes bento-scan {
      0%, 100% { transform: translateY(0); opacity: 0; }
      10% { opacity: 0.9; }
      85% { opacity: 0.9; }
      100% { transform: translateY(200px); opacity: 0; }
    }
    @keyframes figma-a {
      0%   { top: 72%; left: 12%; }
      50%  { top: 22%; left: 68%; }
      100% { top: 72%; left: 12%; }
    }
    @keyframes figma-b {
      0%   { top: 18%; left: 72%; }
      50%  { top: 68%; left: 20%; }
      100% { top: 18%; left: 72%; }
    }
    @keyframes float-a {
      0%   { transform: rotate(-7deg) translateY(0px); }
      100% { transform: rotate(-5deg) translateY(-14px); }
    }
    @keyframes float-b {
      0%   { transform: rotate(7deg) translateY(0px); }
      100% { transform: rotate(9deg) translateY(-18px); }
    }
    @keyframes float-up {
      0%   { transform: translateY(0px); }
      100% { transform: translateY(-12px); }
    }
    @keyframes shimmer {
      0%   { transform: translateX(-160%) skewX(-20deg); }
      100% { transform: translateX(160%) skewX(-20deg); }
    }
    @keyframes clutch-glow {
      0%, 100% { opacity: 0.85; }
      50%       { opacity: 1; text-shadow: 0 0 12px rgba(251,191,36,0.8); }
    }
    @keyframes packet {
      0%   { stroke-dashoffset: 30; }
      100% { stroke-dashoffset: 0; }
    }
    @keyframes terminal-scroll {
      0%, 100% { transform: translateY(0); }
      50%       { transform: translateY(-22px); }
    }
    @keyframes code-type {
      0%, 100% { width: 0; }
      50%       { width: 11em; }
    }
  `;

  return (
    <section className="px-5 sm:px-8 py-20 lg:py-28 bg-[#F8FAFC]" data-testid="bento-services-section">
      <style>{css}</style>
      <div className="max-w-7xl mx-auto">
        <Reveal className="max-w-2xl">
          <SectionLabel>What We Offer</SectionLabel>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-[44px] tracking-tight text-[#01193D] leading-tight">
            Every capability you need, under one roof
          </h2>
          <p className="text-[#475569] text-base lg:text-lg mt-4">
            One integrated team — strategy, design, and engineering that never fall out of sync.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-12">

          {/* ── CARD 1: AI Automation  ─────────────────────────────────────
               LAYOUT: text at top, compact AI visual at bottom  */}
          <div className="md:col-span-1 md:row-span-2">
            <Link
              to="/services"
              data-testid="bento-card-ai"
              className="card-lift group relative flex flex-col justify-between h-full min-h-[380px] md:min-h-[420px] rounded-3xl bg-white border border-slate-100 p-6 pb-0 text-[#01193D] overflow-hidden soft-shadow"
            >
              <ArrowUpRight size={18} className="absolute top-5 right-5 text-[#2563EB] opacity-60 group-hover:opacity-100 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />

              {/* text block */}
              <div className="shrink-0">
                <span className="inline-flex w-10 h-10 rounded-xl bg-[#CAF2F9]/60 items-center justify-center border border-[#91E0F1]/40 shadow-sm">
                  <span className="text-base">🤖</span>
                </span>
                <h3 className="font-display font-semibold text-lg text-[#01193D] mt-3">AI & Business Automation</h3>
                <p className="text-xs text-[#475569] leading-relaxed mt-1.5">
                  LLM integrations and intelligent workflow pipelines engineered to eliminate manual work.
                </p>
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {["LLMs", "RPA", "Pipelines"].map(t => (
                    <span key={t} className="text-[9px] font-semibold bg-[#CAF2F9] text-[#01193D] px-2 py-0.5 rounded-full">{t}</span>
                  ))}
                </div>
              </div>

              {/* AI Automation Graphic — properly scaled to fill lower card area */}
              <div className="flex-1 flex items-end justify-center pt-4 pb-0 overflow-hidden">
                <img
                  src="/aiautomation.png"
                  alt="AI & Business Automation"
                  className="max-h-[250px] sm:max-h-[280px] md:max-h-[300px] w-auto max-w-full object-contain object-bottom block transition-transform duration-300 group-hover:scale-105"
                />
              </div>
            </Link>
          </div>

          {/* ── CARD 2: Custom Software  ───────────────────────────────────
               LAYOUT: text top-left, IDE window BOTTOM-RIGHT flush to bottom edge  */}
          <div className="md:col-span-3">
            <Link
              to="/services"
              data-testid="bento-card-software"
              className="card-lift group relative flex flex-col h-full min-h-[280px] rounded-3xl bg-white border border-slate-100 p-7 text-[#01193D] overflow-hidden soft-shadow"
            >
              <ArrowUpRight size={18} className="absolute top-6 right-6 text-[#2563EB] opacity-40 group-hover:opacity-100 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />

              {/* text — limited to left half so IDE has room */}
              <div className="w-full md:max-w-[46%]">
                <span className="inline-flex w-12 h-12 rounded-2xl bg-[#CAF2F9]/60 items-center justify-center border border-[#91E0F1]/30">
                  <span className="text-xl">💻</span>
                </span>
                <h3 className="font-display font-semibold text-lg text-[#01193D] mt-5">Custom Software</h3>
                <p className="text-sm text-[#475569] leading-relaxed mt-2">
                  Tailored business platforms engineered around your specific operating model and custom API flows.
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {["API Integrations", "Database Arch.", "SaaS Systems"].map(t => (
                    <span key={t} className="text-[10px] font-semibold bg-[#CAF2F9] text-[#01193D] px-2.5 py-0.5 rounded-full">{t}</span>
                  ))}
                </div>
              </div>

              {/* IDE window: absolute, right side, sticks to bottom with NO bottom gap */}
              <div
                className="hidden md:block absolute bottom-0 right-0 w-[52%] h-[210px] bg-slate-900 rounded-tl-2xl rounded-tr-3xl border-t border-l border-slate-700/80 shadow-xl font-mono text-[9px] text-slate-300 overflow-hidden transition-transform duration-300 group-hover:-translate-y-0.5"
              >
                {/* title bar */}
                <div className="flex items-center gap-1.5 border-b border-slate-800 px-4 py-2.5 bg-slate-950/60">
                  <span className="w-2.5 h-2.5 rounded-full bg-rose-500/80" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
                  <span className="ml-3 text-slate-400 text-[8px]">api_cluster.py</span>
                  <span className="ml-auto text-slate-500 text-[7px]">Python 3.11</span>
                </div>
                {/* code area */}
                <div className="px-4 py-3 space-y-1.5 leading-relaxed">
                  <div><span className="text-purple-400">import</span> <span className="text-blue-400">openai, boto3</span></div>
                  <div><span className="text-purple-400">from</span> <span className="text-blue-400">config</span> <span className="text-purple-400">import</span> <span className="text-sky-300">CLUSTER_ID</span></div>
                  <div className="mt-1"><span className="text-purple-400">def</span> <span className="text-yellow-300">auto_scale</span>(<span className="text-orange-300">target</span>):</div>
                  <div className="pl-4"><span className="text-slate-500">scale =</span> <span className="text-amber-400">1.145</span></div>
                  <div className="pl-4 flex items-center gap-0">
                    <span className="text-blue-300">return</span>&nbsp;
                    <span
                      className="text-emerald-400 overflow-hidden whitespace-nowrap border-r-2 border-[#09B2ED]"
                      style={{ animation: "code-type 3.5s steps(22) infinite", display: "inline-block" }}
                    >
                      scale_group(target)
                    </span>
                  </div>
                </div>
                {/* terminal */}
                <div className="absolute bottom-0 inset-x-0 h-[54px] bg-black/70 border-t border-slate-800 px-4 py-2 overflow-hidden">
                  <div className="text-[7.5px] font-mono" style={{ animation: "terminal-scroll 4s ease-in-out infinite" }}>
                    <div className="text-slate-500">$ python api_cluster.py</div>
                    <div className="text-emerald-400">[INFO] Spawning worker node-08…</div>
                    <div className="text-slate-400">[OK]&nbsp; Scaled +14.5% — 3 nodes active</div>
                    <div className="text-[#09B2ED]">[LIVE] Monitoring cluster health…</div>
                  </div>
                </div>
              </div>

              {/* Mobile fallback for card 2 */}
              <div className="md:hidden mt-6 w-full h-[160px] bg-slate-900 rounded-2xl font-mono text-[8.5px] text-slate-300 overflow-hidden flex flex-col">
                <div className="flex items-center gap-1.5 border-b border-slate-800 px-3 py-2 bg-slate-950/60">
                  <span className="w-2 h-2 rounded-full bg-rose-500/80" />
                  <span className="w-2 h-2 rounded-full bg-amber-400/80" />
                  <span className="w-2 h-2 rounded-full bg-emerald-500/80" />
                  <span className="ml-2 text-slate-400 text-[7px]">api_cluster.py</span>
                </div>
                <div className="px-3 py-2 space-y-1 flex-1">
                  <div><span className="text-purple-400">import</span> <span className="text-blue-400">openai, boto3</span></div>
                  <div><span className="text-purple-400">def</span> <span className="text-yellow-300">auto_scale</span>(<span className="text-orange-300">target</span>):</div>
                  <div className="pl-3"><span className="text-slate-500">scale =</span> <span className="text-amber-400">1.145</span></div>
                </div>
                <div className="border-t border-slate-800 px-3 py-1.5 bg-black/50 text-emerald-400 text-[7px]">
                  [OK] Scaled +14.5% — 3 nodes active
                </div>
              </div>
            </Link>
          </div>

          {/* ── CARD 3: Social Proof  ──────────────────────────────────────
               LAYOUT: fully CENTERED, avatar circles + rating  */}
          <div className="md:col-span-1">
            <div className="card-lift group relative flex flex-col justify-center items-center h-full min-h-[220px] rounded-3xl bg-[#CAF2F9]/30 border border-[#91E0F1]/30 p-7 text-[#01193D]">
              <div className="flex flex-col items-center gap-4">
                <div className="flex -space-x-3">
                  {[
                    ["M", "bg-[#2563EB] text-white"],
                    ["N", "bg-[#09B2ED] text-white"],
                    ["C", "bg-[#01193D] text-white"],
                    ["+", "bg-white border-2 border-[#2563EB]/30 text-[#2563EB]"],
                  ].map(([label, cls], i) => (
                    <div
                      key={i}
                      className={`w-11 h-11 rounded-full border-2 border-[#F8FAFC] flex items-center justify-center text-[11px] font-bold shadow-md transition-transform duration-300 group-hover:scale-105 ${cls}`}
                    >
                      {label}
                    </div>
                  ))}
                </div>
                <div className="text-center">
                  <div className="text-[#2563EB] font-display font-bold text-xl">150+ Clients</div>
                  <div className="text-[11px] text-amber-500 uppercase tracking-widest mt-1.5 font-mono">
                    ★★★★★ Clutch 5.0
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── CARD 4: Web & Mobile  ──────────────────────────────────────
               LAYOUT: text top-left, image in right half  */}
          <div className="md:col-span-2">
            <Link
              to="/services"
              data-testid="bento-card-mobile"
              className="card-lift group relative flex flex-col h-full min-h-[280px] rounded-3xl bg-[#0A2B5C] p-7 text-white overflow-hidden shadow-xl"
            >
              <ArrowUpRight size={18} className="absolute top-6 right-6 text-[#09B2ED] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />

              {/* text block — left side */}
              <div className="w-full md:max-w-[46%] z-10">
                <span className="inline-flex w-12 h-12 rounded-2xl bg-white/10 items-center justify-center">
                  <span className="text-xl">📱</span>
                </span>
                <h3 className="font-display font-semibold text-lg text-white mt-5">Web & Mobile Apps</h3>
                <p className="text-xs text-slate-300 leading-relaxed mt-2">
                  High-performance apps matching native device controls.
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {["React Native", "iOS · Android"].map(t => (
                    <span key={t} className="text-[9px] font-semibold bg-white/10 text-[#91E0F1] px-2.5 py-0.5 rounded-full">{t}</span>
                  ))}
                </div>
              </div>

              {/* Web & Mobile Apps Graphic — Desktop */}
              <div className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-[50%] items-center justify-center">
                <img
                  src="/webmobileapps.png"
                  alt="Web & Mobile Apps Showcase"
                  className="w-full max-w-[340px] h-auto object-contain drop-shadow-2xl transition-transform duration-300 group-hover:scale-105"
                />
              </div>

              {/* Mobile Display */}
              <div className="md:hidden mt-6 flex justify-center w-full">
                <img
                  src="/webmobileapps.png"
                  alt="Web & Mobile Apps Showcase"
                  className="w-full max-w-[260px] h-auto object-contain drop-shadow-xl"
                />
              </div>
            </Link>
          </div>

          {/* ── CARD 5: UI/UX & Branding  ─────────────────────────────────
               LAYOUT: text top-left, design canvas BOTTOM-RIGHT flush to bottom edge  */}
          <div className="md:col-span-2">
            <Link
              to="/services"
              data-testid="bento-card-uiux"
              className="card-lift group relative flex flex-col h-full min-h-[280px] rounded-3xl bg-gradient-to-br from-[#2563EB] to-[#09B2ED] p-7 text-white overflow-hidden shadow-xl"
            >
              <ArrowUpRight size={18} className="absolute top-6 right-6 text-white/80 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />

              {/* text block */}
              <div className="w-full md:max-w-[46%]">
                <span className="inline-flex w-12 h-12 rounded-2xl bg-white/20 items-center justify-center">
                  <span className="text-xl">🎨</span>
                </span>
                <h3 className="font-display font-semibold text-lg text-white mt-5">UI/UX & Branding</h3>
                <p className="text-xs text-white/80 leading-relaxed mt-2">
                  Design systems built to foster adoption and brand trust.
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {["Design Systems", "Figma"].map(t => (
                    <span key={t} className="text-[9px] font-semibold bg-white/20 text-white px-2.5 py-0.5 rounded-full">{t}</span>
                  ))}
                </div>
              </div>

              {/* Figma-like canvas: absolute, right side, FLUSH TO BOTTOM */}
              <div
                className="hidden md:block absolute bottom-0 right-0 w-[52%] h-[195px] bg-slate-900 rounded-tl-2xl rounded-tr-3xl border-t border-l border-white/10 overflow-hidden"
              >
                {/* toolbar */}
                <div className="flex items-center gap-2 border-b border-slate-800 px-4 py-2 bg-slate-950/70">
                  <div className="text-[7px] text-slate-400 font-mono uppercase tracking-wider">Figma — Design</div>
                  <div className="ml-auto flex gap-1.5">
                    <span className="text-[6px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded font-mono">Alice ●</span>
                    <span className="text-[6px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded font-mono">Bob ●</span>
                  </div>
                </div>

                {/* canvas area */}
                <div className="relative flex-1 h-[calc(100%-32px)] p-4">
                  {/* moving cursors */}
                  <div className="absolute w-2 h-2 z-30" style={{ animation: "figma-a 5s ease-in-out infinite" }}>
                    <svg width="12" height="16" viewBox="0 0 12 16"><path d="M0 0l4 14 2.5-4 4 2z" fill="#34d399"/></svg>
                    <span className="absolute left-3 top-0 bg-emerald-500 text-[6px] px-1 py-0.5 rounded text-white font-mono whitespace-nowrap">Alice</span>
                  </div>
                  <div className="absolute w-2 h-2 z-30" style={{ animation: "figma-b 5s ease-in-out infinite" }}>
                    <svg width="12" height="16" viewBox="0 0 12 16"><path d="M0 0l4 14 2.5-4 4 2z" fill="#fbbf24"/></svg>
                    <span className="absolute left-3 top-0 bg-amber-500 text-[6px] px-1 py-0.5 rounded text-white font-mono whitespace-nowrap">Bob</span>
                  </div>

                  {/* shimmer sweep */}
                  <div className="absolute inset-0 w-[35%] h-full bg-gradient-to-r from-transparent via-white/8 to-transparent pointer-events-none"
                    style={{ animation: "shimmer 2.8s infinite" }} />

                  {/* artboard */}
                  <div className="border border-dashed border-white/25 rounded-xl p-3 h-full flex flex-col justify-between relative">
                    <div className="text-[7.5px] font-mono text-slate-400">HeroSection / v3</div>
                    <div className="space-y-2">
                      <div className="h-3 bg-[#2563EB]/60 rounded-md w-[70%]" />
                      <div className="h-2 bg-white/10 rounded-md w-[90%]" />
                      <div className="h-2 bg-white/10 rounded-md w-[60%]" />
                      <div className="flex gap-2 mt-2">
                        <div className="h-5 w-14 bg-[#2563EB] rounded-lg" />
                        <div className="h-5 w-14 bg-white/10 rounded-lg border border-white/20" />
                      </div>
                    </div>
                    <div className="text-[7px] text-slate-500 font-mono flex justify-between">
                      <span>1440 × 900</span><span>Components: 12</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile fallback */}
              <div className="md:hidden mt-6 w-full h-[140px] bg-slate-900 rounded-2xl overflow-hidden">
                <div className="border-b border-slate-800 px-3 py-2 text-[7px] text-slate-400 font-mono">HeroSection / v3</div>
                <div className="p-3 space-y-2">
                  <div className="h-2.5 bg-[#2563EB]/60 rounded w-[65%]" />
                  <div className="h-2 bg-white/10 rounded w-[85%]" />
                  <div className="h-2 bg-white/10 rounded w-[55%]" />
                  <div className="flex gap-2 mt-2"><div className="h-4 w-12 bg-[#2563EB] rounded" /><div className="h-4 w-12 bg-white/10 rounded border border-white/20" /></div>
                </div>
              </div>
            </Link>
          </div>

          {/* ── CARD 6: Cloud & Analytics  ────────────────────────────────
               LAYOUT: text top-left, server network CENTERED vertically in right half  */}
          <div className="md:col-span-2">
            <Link
              to="/services"
              data-testid="bento-card-cloud"
              className="card-lift group relative flex flex-col h-full min-h-[280px] rounded-3xl bg-white border border-slate-100 p-7 text-[#01193D] overflow-hidden soft-shadow"
            >
              <ArrowUpRight size={18} className="absolute top-6 right-6 text-[#2563EB] opacity-40 group-hover:opacity-100 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />

              {/* text block */}
              <div className="w-full md:max-w-[46%]">
                <span className="inline-flex w-12 h-12 rounded-2xl bg-[#CAF2F9]/60 items-center justify-center border border-[#91E0F1]/30">
                  <span className="text-xl">📈</span>
                </span>
                <h3 className="font-display font-semibold text-lg text-[#01193D] mt-5">Cloud & Analytics</h3>
                <p className="text-xs text-[#475569] leading-relaxed mt-2">
                  DevOps infrastructure coupled with performance data pipelines.
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {["AWS", "SEO & Analytics"].map(t => (
                    <span key={t} className="text-[9px] font-semibold bg-[#CAF2F9] text-[#01193D] px-2.5 py-0.5 rounded-full">{t}</span>
                  ))}
                </div>
              </div>

              {/* Cloud node panel — absolute, centered in the right half */}
              <div className="hidden md:block absolute right-6 top-1/2 -translate-y-1/2 w-[46%]">
                <div
                  className="bg-[#F0F7FF] border border-slate-200 rounded-2xl shadow-xl overflow-hidden"
                  style={{ animation: "float-up 3s ease-in-out infinite alternate-reverse" }}
                >
                  {/* header */}
                  <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-100">
                    <span className="text-[9px] font-bold text-slate-500 font-mono uppercase tracking-wider">Cloud Node-02</span>
                    <span className="text-[9px] font-bold text-emerald-500 font-mono">● ACTIVE</span>
                  </div>

                  {/* node network SVG */}
                  <div className="px-4 py-5">
                    <svg className="w-full h-14" viewBox="0 0 160 40" fill="none">
                      {/* bg lines */}
                      <line x1="20" y1="20" x2="80" y2="20" stroke="#CBD5E1" strokeWidth="2.5"/>
                      <line x1="80" y1="20" x2="140" y2="20" stroke="#CBD5E1" strokeWidth="2.5"/>
                      {/* animated data flow */}
                      <line x1="20" y1="20" x2="140" y2="20" stroke="#09B2ED" strokeWidth="2.5" strokeDasharray="10,30"
                        style={{ animation: "packet 1.5s linear infinite" }}/>
                      {/* nodes */}
                      <circle cx="20"  cy="20" r="9"  fill="#2563EB"/>
                      <circle cx="80"  cy="20" r="9"  fill="#09B2ED" className="animate-pulse"/>
                      <circle cx="140" cy="20" r="9"  fill="#01193D"/>
                      {/* node labels */}
                      <text x="20"  y="36" textAnchor="middle" fontSize="6" fill="#64748B" fontFamily="monospace">US-East</text>
                      <text x="80"  y="36" textAnchor="middle" fontSize="6" fill="#64748B" fontFamily="monospace">EU-West</text>
                      <text x="140" y="36" textAnchor="middle" fontSize="6" fill="#64748B" fontFamily="monospace">AP-South</text>
                    </svg>
                  </div>

                  {/* metrics row */}
                  <div className="flex justify-between items-center px-4 pb-4 text-[9px] font-semibold font-mono">
                    <div className="text-center">
                      <div className="text-slate-400 text-[7.5px]">CPU</div>
                      <div className="text-[#01193D]">42%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-slate-400 text-[7.5px]">Latency</div>
                      <div className="text-[#01193D]">14ms</div>
                    </div>
                    <div className="text-center">
                      <div className="text-slate-400 text-[7.5px]">Uptime</div>
                      <div className="text-emerald-600">99.9%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-slate-400 text-[7.5px]">Savings</div>
                      <div className="text-[#2563EB]">$2.4k</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile fallback */}
              <div className="md:hidden mt-6 bg-[#F0F7FF] border border-slate-200 rounded-2xl overflow-hidden">
                <div className="flex justify-between px-4 py-2.5 bg-white border-b border-slate-100">
                  <span className="text-[9px] font-bold text-slate-500 font-mono">Cloud Node-02</span>
                  <span className="text-[9px] font-bold text-emerald-500 font-mono">● ACTIVE</span>
                </div>
                <div className="px-4 py-4 flex justify-around text-[9px] font-mono font-semibold">
                  <div className="text-center"><div className="text-slate-400 text-[8px]">CPU</div><div>42%</div></div>
                  <div className="text-center"><div className="text-slate-400 text-[8px]">Latency</div><div>14ms</div></div>
                  <div className="text-center"><div className="text-slate-400 text-[8px]">Uptime</div><div className="text-emerald-600">99.9%</div></div>
                  <div className="text-center"><div className="text-slate-400 text-[8px]">Savings</div><div className="text-[#2563EB]">$2.4k</div></div>
                </div>
              </div>
            </Link>
          </div>

        </div>
      </div>
    </section>
  );
}
